import { Anchor, AppShell, Box, Button, Card, Container, Group, Paper, SegmentedControl, Stack, Text, Title, Tooltip, Typography } from "@mantine/core";
import { IconArrowLeft, IconBook2, IconBuildingBank, IconChartBar, IconLogin, IconUserPlus } from "@tabler/icons-react";
import { marked } from "marked";
import { useEffect, useState } from "react";

import { login, register } from "./auth/keycloak";
import { BrandMarks, BrandTitle, useBranding } from "./branding";
import { useT } from "./i18n/script";
import StatsPage from "./pages/public/StatsPage";

/** Javna naslovna: prijava (zaposleni i registrovani) i registracija gazdinstva za eksterne korisnike; javna statistika. */
export default function PublicApp() {
  const { t, script, setScript } = useT();
  const brand = useBranding();
  const path = window.location.pathname;
  const [view, setView] = useState<"home" | "stats" | "guide">(
    path.startsWith("/statistika") ? "stats" : path.startsWith("/uputstvo-registracija") ? "guide" : "home");

  if (view === "guide") {
    return (
      <AppShell header={{ height: 56 }} padding="md">
        <AppShell.Header style={{ background: "#1f4d36", borderBottom: "none" }}>
          <Group h="100%" px="md" justify="space-between" wrap="nowrap">
            <Button variant="subtle" color="white" leftSection={<IconArrowLeft size={16} />} onClick={() => { history.replaceState(null, "", "/"); setView("home"); }}>AgriSense</Button>
            <Group gap="xs">
              <Button size="xs" color="red" variant="white" leftSection={<IconUserPlus size={14} />} onClick={register}>{t("Registracija gazdinstva")}</Button>
              <Button size="xs" color="lime" leftSection={<IconLogin size={14} />} onClick={login}>{t("Prijava")}</Button>
            </Group>
          </Group>
        </AppShell.Header>
        <AppShell.Main><Container size="md"><PublicGuide /></Container></AppShell.Main>
      </AppShell>
    );
  }

  if (view === "stats") {
    return (
      <AppShell header={{ height: 56 }} padding="md">
        <AppShell.Header style={{ background: "#1f4d36", borderBottom: "none" }}>
          <Group h="100%" px="md" justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <Button variant="subtle" color="white" px={6} onClick={() => setView("home")}><IconArrowLeft size={16} /></Button>
              <BrandMarks h={34} onDark />
              <Box visibleFrom="sm"><BrandTitle extra="AgriSense" /></Box>
            </Group>
            <Button size="xs" color="lime" leftSection={<IconLogin size={14} />} onClick={login}>{t("Prijava")}</Button>
          </Group>
        </AppShell.Header>
        <AppShell.Main><Container size="xl"><StatsPage /></Container></AppShell.Main>
      </AppShell>
    );
  }

  return (
    <Box className="landing">
      <div className="landing-bg" aria-hidden />
      <Container size="lg" py={{ base: "md", md: 40 }} pos="relative">
        <Group justify="space-between" mb={{ base: "lg", md: 48 }}>
          <span />
          <SegmentedControl size="xs" value={script} onChange={(v) => setScript(v as "lat" | "cyr")}
            data={[{ value: "lat", label: "Lat" }, { value: "cyr", label: "Ћир" }]} />
        </Group>

        <Stack align="center" gap="xl" mt={{ base: 40, md: 90 }}>
          <BrandMarks h={88} onDark />
          <Stack gap={2} align="center" mt={-8}>
            <Text c="#d6ebcf" ta="center" size="xs" tt="uppercase" style={{ letterSpacing: ".08em" }}>
              {t(brand.nadredjeni || "Republika Srpska")}{brand.ministarstvo ? ` · ${t(brand.ministarstvo)}` : ""}
            </Text>
            <Text c="white" ta="center" fw={700} size="lg">{t(brand.naziv)}</Text>
          </Stack>
          <Title c="white" ta="center" fz={{ base: 34, md: 52 }} lh={1.05} fw={800} mt={-8} translate="no">Agri<span style={{ color: "#b6e3a8" }}>Sense</span></Title>
          <Text c="#d6ebcf" ta="center" size="lg" mt={-14}>{t("Registar i praćenje voćnjaka")}</Text>
          <Card radius="lg" padding="xl" shadow="xl" w="100%" maw={420}>
            <Stack gap="sm">
              <Button fullWidth size="md" color="agri" leftSection={<IconLogin size={18} />} onClick={login}>{t("Prijavi se")}</Button>
              <Tooltip multiline w={300} withArrow position="bottom"
                label={t("Za nosioce gazdinstava: nalog, potvrda e-pošte, unos podataka gazdinstva i verifikacija u Agenciji.")}>
                <Button fullWidth size="md" variant="light" color="red" leftSection={<IconUserPlus size={18} />} onClick={register}>
                  {t("Registracija gazdinstva")}
                </Button>
              </Tooltip>
              <Anchor size="sm" ta="center" href="/uputstvo-registracija" onClick={(e) => { e.preventDefault(); history.pushState(null, "", "/uputstvo-registracija"); setView("guide"); }}>
                <Group gap={4} justify="center"><IconBook2 size={14} />{t("Uputstvo za registraciju (primjer korak po korak, e-pošta)")}</Group>
              </Anchor>
              <Anchor size="sm" ta="center" onClick={() => setView("stats")}>
                <Group gap={4} justify="center"><IconChartBar size={14} />{t("Javna statistika voćarstva")}</Group>
              </Anchor>
            </Stack>
          </Card>
        </Stack>

        <Group justify="center" mt={48} gap="xs">
          <IconBuildingBank size={14} color="#cfe3cf" />
          <Text size="xs" c="#cfe3cf">© {new Date().getFullYear()} {t("Agencija za agrarna plaćanja Republike Srpske")} · AgriSense v0.5</Text>
        </Group>
      </Container>
    </Box>
  );
}

/** Javni dio uputstva (registracija eksternog korisnika i e-pošta) — dostupan bez prijave. */
function PublicGuide() {
  const { t } = useT();
  const [secs, setSecs] = useState<{ id: string; title: string; markdown: string }[] | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    fetch("/api/v1/help/public").then((r) => (r.ok ? r.json() : Promise.reject())).then(setSecs).catch(() => setErr(true));
  }, []);
  if (err) return <Text c="red" mt="md">{t("Uputstvo trenutno nije dostupno.")}</Text>;
  return (
    <Stack mt="md">
      {(secs ?? []).map((s) => (
        <Paper key={s.id} withBorder radius="md" p="lg">
          <Title order={2} mb="sm">{t(s.title)}</Title>
          <Typography><div dangerouslySetInnerHTML={{ __html: marked.parse(s.markdown, { async: false }) as string }} /></Typography>
        </Paper>
      ))}
    </Stack>
  );
}
